import { CartAddEvent, CartUpdateEvent } from "./events.js";

/**
 * Upsell Drawer Component
 *
 * Displays upsell products after adding a product to cart.
 * Fetches upsell products from product metafields and allows
 * users to add them to cart or continue to cart.
 */
class UpsellDrawerComponent extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.currentProductId = null;
    this.addedProduct = null;
    this.previousCartState = null; // Store previous cart state to identify new items
    this.isProcessingCartAdd = false;
  }

  connectedCallback() {
    // Get references to elements
    this.overlay = this.querySelector('[ref="overlay"]');
    this.dialog = this.querySelector('[ref="dialog"]');
    this.closeButton = this.querySelector('[ref="closeButton"]');
    this.continueButton = this.querySelector('[ref="continueButton"]');
    this.productsContainer = this.querySelector('[ref="productsContainer"]');
    this.addedProductContainer = this.querySelector(
      '[ref="addedProductContainer"]'
    );

    this.addEventListener("click", this.#handleClick);
    this.closeButton?.addEventListener("click", this.#close.bind(this));
    this.continueButton?.addEventListener(
      "click",
      this.#continueToCart.bind(this)
    );

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    document.addEventListener("keydown", this.#handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener("keydown", this.#handleKeydown);
  }

  /**
   * Handles cart add events to show upsell products
   * @param {CartAddEvent} event - The cart add event
   */
  #handleCartAdd = async (event) => {
    console.log("Cart add event received:", event.detail);

    // Prevent infinite recursion by checking if we're already processing this event
    if (this.isProcessingCartAdd) {
      console.log("Already processing cart add event, skipping");
      return;
    }

    // Check for different possible product identifiers
    const data = event.detail.data;
    const productId = data.productId || data.handle || data.id;
    const source = data.source;

    console.log("Cart add data:", { productId, source, fullData: data });
    
    // Skip processing if this event doesn't have a valid source
    // This prevents processing events from URL-based cart additions
    if (!source) {
      console.log("Cart add event has no source, skipping upsell drawer processing");
      return;
    }

    // Allow volume discount table to trigger upsell drawer
    // This provides a better UX than URL redirects
    
    // Log timing for sticky add to cart
    if (source === 'sticky-add-to-cart') {
      console.log("Sticky add to cart event received at:", new Date().toISOString());
    }

    // Allow sticky add-to-cart events to trigger upsell drawer
    // (removed skip logic to enable upsell drawer for sticky add to cart)

    if (productId) {
      console.log("Processing cart add for product:", productId);
      this.isProcessingCartAdd = true;
      this.currentProductId = productId;
      
      // Get the latest cart data to ensure we have the most up-to-date information
      try {
        // Add a small delay to ensure the cart has been updated
        // Optimize delay for sticky add to cart - use shorter delay for better UX
        const delay = source === 'sticky-add-to-cart' ? 50 : 100;
        console.log(`Using ${delay}ms delay for source: ${source}`);
        await new Promise(resolve => setTimeout(resolve, delay));
        
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        
        console.log("Full cart data:", cartData);
        
        // Find the most recently added item by comparing with previous cart state
        // or use the last item if we can't determine the order
        let latestItem = null;
        
        // First, try to find an item that matches the product ID from the event
        if (productId) {
          latestItem = cartData.items.find(item => 
            item.product_id.toString() === productId.toString() ||
            item.handle === productId ||
            item.id.toString() === productId.toString()
          );
        }
        
        // If we have a previous cart state, try to find the newly added item
        if (!latestItem && this.previousCartState) {
          const newItems = cartData.items.filter(currentItem => 
            !this.previousCartState.items.some(prevItem => 
              prevItem.variant_id === currentItem.variant_id
            )
          );
          if (newItems.length > 0) {
            latestItem = newItems[0]; // Take the first new item
          }
        }
        
        // If still not found, use the last item in cart
        if (!latestItem && cartData.items.length > 0) {
          latestItem = cartData.items[cartData.items.length - 1];
        }
        
        // Store current cart state for next comparison
        this.previousCartState = cartData;
        
        if (latestItem) {
          console.log("Selected cart item:", latestItem);
          console.log("Product ID from event:", productId);
          console.log("Product ID from cart item:", latestItem.product_id);
          
          this.addedProduct = {
            productId: latestItem.product_id.toString(),
            variantId: latestItem.variant_id.toString(),
            itemCount: latestItem.quantity,
            title: latestItem.product_title,
            price: latestItem.final_price,
            image: latestItem.image,
            variant: latestItem.variant_title
          };
        } else {
          console.log("No matching item found in cart, using event data");
          // Fallback to event data if cart fetch fails
          this.addedProduct = event.detail.data;
        }
      } catch (error) {
        console.error("Error fetching cart data:", error);
        // Fallback to event data
        this.addedProduct = event.detail.data;
      }
      
      this.#showAddedProduct();
      this.#fetchAndShowUpsellProducts(productId);

      setTimeout(() => {
        this.isProcessingCartAdd = false;
      }, 100);
    } else {
      console.log("No product identifier found in cart add event");
    }
  };

  /**
   * Shows the added to cart product information
   */
  async #showAddedProduct() {
    console.log("showAddedProduct called", {
      addedProduct: this.addedProduct,
      container: this.addedProductContainer,
    });

    if (!this.addedProduct || !this.addedProductContainer) {
      console.log("Missing required data:", {
        addedProduct: !!this.addedProduct,
        container: !!this.addedProductContainer,
      });
      return;
    }

    // Check for different possible product identifiers
    const productId =
      this.addedProduct.productId ||
      this.addedProduct["handle"] ||
      this.addedProduct["id"];
    const variantId = this.addedProduct.variantId;
    const quantity = this.addedProduct.itemCount || 1;


    if (!productId) return;

    try {
      // Always try to extract product data from page first to get English title
      const productData = this.#extractProductDataFromPage(
        productId,
        variantId
      );

      if (productData && productData.title) {
        console.log("Using extracted product data from page:", productData);
        
        // Use page data for title, but fall back to cart data for price and image if needed
        const title = productData.title;
        const price = productData.price || this.#formatMoney(this.addedProduct.price || 0);
        const image = productData.image || this.addedProduct.image;
        const variant = productData.variant || this.addedProduct.variant;
        
        // Get translation strings
        const globalStrings = window.themeStrings || {};
        const itemAddedText = globalStrings.item_added_to_cart || "Item added to cart";
        const quantityText = globalStrings.quantity_label || "Quantity";
        
        const addedProductHTML = `
          <div class="upsell-added-product">
            <h3 class="upsell-added-product__header">${itemAddedText}</h3>
            <div class="upsell-added-product__wrapper">
              <div class="upsell-added-product__image">
                <img 
                  src="${image}" 
                  alt="${title}"
                  loading="lazy"
                />
              </div>
              <div class="upsell-added-product__content">
                <h3 class="upsell-added-product__title">${title}</h3>
                <p class="upsell-added-product__price">${price}</p>
                ${variant ? `<p class="upsell-added-product__variant">${variant}</p>` : ''}
                <p class="upsell-added-product__quantity">${quantityText}: ${this.addedProduct.itemCount}</p>
              </div>
            </div>
          </div>
        `;

        this.addedProductContainer.innerHTML = addedProductHTML;
      } else if (this.addedProduct.title && this.addedProduct.price) {
        // Fallback to cart data if page extraction fails
        console.log("Using cart data for added product:", this.addedProduct);
        
        // Format the price properly
        const formattedPrice = this.#formatMoney(this.addedProduct.price);
        
        // Get translation strings
        const globalStrings = window.themeStrings || {};
        const itemAddedText = globalStrings.item_added_to_cart || "Item added to cart";
        const quantityText = globalStrings.quantity_label || "Quantity";
        
        const addedProductHTML = `
          <div class="upsell-added-product">
            <h3 class="upsell-added-product__header">${itemAddedText}</h3>
            <div class="upsell-added-product__wrapper">
              <div class="upsell-added-product__image">
                <img 
                  src="${this.addedProduct.image}" 
                  alt="${this.addedProduct.title}"
                  loading="lazy"
                />
              </div>
              <div class="upsell-added-product__content">
                <h3 class="upsell-added-product__title">${this.addedProduct.title}</h3>
                <p class="upsell-added-product__price">${formattedPrice}</p>
                ${this.addedProduct.variant ? `<p class="upsell-added-product__variant">${this.addedProduct.variant}</p>` : ''}
                <p class="upsell-added-product__quantity">${quantityText}: ${this.addedProduct.itemCount}</p>
              </div>
            </div>
          </div>
        `;

        this.addedProductContainer.innerHTML = addedProductHTML;
      } else {
        console.log("No product data extracted, trying API fallback");
        await this.#fetchProductDataFromAPI(productId, variantId, quantity);
      }
    } catch (error) {
      console.error("Error getting product data:", error);
      
      // Fallback: show a basic success message even if we can't get product details
      const globalStrings = window.themeStrings || {};
      const itemAddedText = globalStrings.item_added_to_cart || "Item added to cart";
      
      const fallbackHTML = `
        <div class="upsell-added-product">
          <h3 class="upsell-added-product__header">${itemAddedText}</h3>
          <div class="upsell-added-product__wrapper">
            <div class="upsell-added-product__content">
              <p>Product succesvol toegevoegd aan je winkelwagen.</p>
            </div>
          </div>
        </div>
      `;
      this.addedProductContainer.innerHTML = fallbackHTML;
    }
  }

  /**
   * Fetches and displays upsell products for the given product ID
   * @param {string} productId - The product ID to fetch upsell products for
   */
  async #fetchAndShowUpsellProducts(productId) {
    console.log("fetchAndShowUpsellProducts called for productId:", productId);
    
    try {
      // Extract upsell products from the current page
      const allUpsellProducts = this.#extractUpsellProducts(document);
      console.log("All upsell products found:", allUpsellProducts);

      // Filter to only show upsell products for the specific product that was added
      const upsellProducts = this.#filterUpsellProductsForProduct(
        allUpsellProducts,
        productId
      );
      console.log("Filtered upsell products for this product:", upsellProducts);

      if (upsellProducts.length > 0) {
        console.log("Displaying upsell products");
        this.#displayUpsellProducts(upsellProducts);
        this.#open();
      } else {
        console.log("No upsell products found for this product");
        
        // Check if there are any upsell elements at all
        const allUpsellElements = document.querySelectorAll("[data-upsell-product]");
        const debugElements = document.querySelectorAll("[data-debug='no-upsell-products']");
        
        console.log("Debug info:", {
          totalUpsellElements: allUpsellElements.length,
          debugElements: debugElements.length,
          productId: productId
        });
        
        // Still open the drawer to show the added product
        this.#open();
      }
    } catch (error) {
      console.error("Error fetching upsell products:", error);
    }
  }

  /**
   * Extracts upsell products from the product page HTML
   * @param {Document} doc - The parsed product page document
   * @returns {Array} Array of upsell product objects
   */
  #extractUpsellProducts(doc) {
    console.log("Extracting upsell products from document");
    const upsellProducts = [];

    // Look for upsell products in metafields
    const upsellElements = doc.querySelectorAll("[data-upsell-product]");
    console.log("Found upsell elements:", upsellElements.length);

    upsellElements.forEach((element) => {
      const parentProductId = element.getAttribute("data-parent-product-id");
      const productId = element.getAttribute("data-upsell-product-id");
      const variantId = element.getAttribute("data-upsell-product-variant-id");
      const title = element.getAttribute("data-upsell-product-title");
      const price = element.getAttribute("data-upsell-product-price");
      const image = element.getAttribute("data-upsell-product-image");
      const url = element.getAttribute("data-upsell-product-url");
      const piecesPerBox = element.getAttribute(
        "data-upsell-product-pieces-per-box"
      );

      if (productId && title) {
        upsellProducts.push({
          parentProductId: parentProductId,
          id: productId,
          variantId: variantId || productId, // Use variant ID if available, fallback to product ID
          title: title,
          price: price || "",
          image: image || "",
          url: url || `/products/product-${productId}`,
          piecesPerBox: piecesPerBox || "",
        });
      }
    });

    return upsellProducts;
  }

  /**
   * Filters upsell products to only show those for the specific product that was added to cart
   * @param {Array} allUpsellProducts - All upsell products found on the page
   * @param {string} addedProductId - The ID of the product that was added to cart
   * @returns {Array} Filtered upsell products for the specific product
   */
  #filterUpsellProductsForProduct(allUpsellProducts, addedProductId) {
    return allUpsellProducts.filter((product) => {
      return product.parentProductId === addedProductId;
    });
  }

  /**
   * Displays upsell products in the drawer
   * @param {Array} products - Array of upsell product objects
   */
  #displayUpsellProducts(products) {
    if (!this.productsContainer) return;

    // Get global strings from window variable
    const globalStrings = window.themeStrings || {};
    const addToCartText = globalStrings.addToCart || "Add to cart";
    const cartIcons = window.cartIcons || {};
    const addedText = globalStrings.added || "Added";

    this.productsContainer.innerHTML = products
      .map(
        (product) => `
      <div class="upsell-product" data-product-id="${product.id}">
        <img 
          src="${product.image}" 
          alt="${product.title}"
          class="upsell-product__image"
          loading="lazy"
        >
        <div class="upsell-product__content">
          <h3 class="upsell-product__title">${product.title}</h3>
          ${
            product.piecesPerBox
              ? `
            <div class="upsell-product__pieces">
              <span class="volume_stocks nf-flex nf-items-center nf-gap-2">
                <span class="icon-box">
                  <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.50041 6L10.5011 3.00074L5.50182 0L0.501099 3.00074L5.50041 6Z" fill="#111827" fill-opacity="0.5"/>
                    <path d="M0 4V9.12929L5.00001 12V6.87071L0 4Z" fill="#111827" fill-opacity="0.5"/>
                    <path d="M6 6.87071V12L11 9.12929V4L6 6.87071Z" fill="#111827" fill-opacity="0.5"/>
                  </svg>
                </span>
                <span>${product.piecesPerBox} stuks</span>
              </span>
            </div>
          `
              : ""
          }
          <p class="upsell-product__price">${product.price}</p>
          <p class="upsell-product__price-vat">${this.#calculateVatPrice(
            product.price
          )}</p>
          
          <product-form-component
            data-section-id="upsell-drawer-${product.id}"
            data-product-id="${product.id}"
            data-product-url="${product.url}"
            on:submit="/handleSubmit"
            data-quantity-default="1"
          >
            <div
              class="visually-hidden"
              aria-live="assertive"
              role="status"
              aria-atomic="true"
              ref="liveRegion"
            ></div>
            <form 
              action="/cart/add" 
              method="post" 
              enctype="multipart/form-data"
              data-type="add-to-cart-form"
            >
              <input type="hidden" name="id" value="${
                product.variantId || product.id
              }" ref="variantId">
              <input type="hidden" name="quantity" value="1">
              <add-to-cart-component>
                <button 
                  type="submit" 
                  name="add"
                  ref="addToCartButton"
                  on:click="/handleClick"
                  class="upsell-product__button"
                >
                  <span class="add-to-cart-text">
                    <span class="svg-wrapper add-to-cart-icon">
                      ${cartIcons.addToCart}
                    </span>
                    <span class="add-to-cart-text__content">
                      ${addToCartText}
                    </span>
                  </span>
                  <span aria-hidden="true" class="add-to-cart-text--added">
                    <span class="svg-wrapper add-to-cart-icon--added">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 4L6 11L3 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </span>
                    <span>${addedText}</span>
                  </span>
                </button>
              </add-to-cart-component>
            </form>
          </product-form-component>
        </div>
      </div>
    `
      )
      .join("");
  }

  /**
   * Opens the upsell drawer
   */
  #open() {
    console.log("Upsell drawer opening at:", new Date().toISOString());
    this.isOpen = true;
    this.classList.add("is-open");
    document.body.style.overflow = "hidden";

    // Focus the close button for accessibility
    this.closeButton?.focus();
  }

  /**
   * Closes the upsell drawer
   */
  #close() {
    console.log("Closing upsell drawer");
    this.isOpen = false;
    this.classList.remove("is-open");
    document.body.style.overflow = "";

    // Clear the products container
    if (this.productsContainer) {
      this.productsContainer.innerHTML = "";
    }

    // Clear the added product container
    if (this.addedProductContainer) {
      this.addedProductContainer.innerHTML = "";
    }

    // Reset the added product
    this.addedProduct = null;
  }

  /**
   * Continues to cart (closes drawer)
   */
  #continueToCart() {
    console.log("Continuing to cart");
    this.#close();
  }

  /**
   * Handles clicks within the drawer
   * @param {Event} event - The click event
   */
  #handleClick = (event) => {
    // Close drawer when clicking overlay
    if (event.target === this.overlay) {
      this.#close();
    }
  };

  /**
   * Handles keyboard events
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleKeydown = (event) => {
    if (event.key === "Escape" && this.isOpen) {
      this.#close();
    }
  };

  /**
   * Formats money value
   * @param {number} amount - The amount in cents
   * @returns {string} Formatted money string
   */
  #formatMoney(amount) {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(amount / 100);
  }

  /**
   * Formats variant options for display
   * @param {Object} variant - The variant object
   * @returns {string} Formatted variant options string
   */
  #formatVariantOptions(variant) {
    if (!variant || !variant.option1) return "";

    const options = [];

    if (variant.option1) {
      options.push(variant.option1);
    }
    if (variant.option2) {
      options.push(variant.option2);
    }
    if (variant.option3) {
      options.push(variant.option3);
    }

    return options.join(" / ");
  }

/**
 * Calculates VAT-included price based on domain
 * (e.g., .nl = 21%, .de = 19%)
 * @param {string} priceString - Price string like "€19.75"
 * @returns {string} VAT-included price string
 */
#calculateVatPrice(priceString) {
  try {
    // Extract numeric value from price string (remove currency symbol and spaces)
    const numericPrice = parseFloat(
      priceString.replace(/[^\d,.-]/g, "").replace(",", ".")
    );
    if (isNaN(numericPrice)) return priceString;

    // Determine VAT rate based on domain
    const hostname = typeof window !== "undefined" ? window.location.hostname : "";
    let vatRate = 0.21; // Default: Netherlands

    if (hostname.endsWith(".de")) {
      vatRate = 0.19; // Germany
    } // Add more countries if needed

    // Calculate VAT-included price
    const vatPrice = numericPrice * (1 + vatRate);

    // Format the price back to currency format
    const formattedPrice = this.#formatMoney(vatPrice * 100); // Convert to cents for formatMoney

    // Add localized VAT text
    const vatLabel =
      hostname.endsWith(".de") ? "inkl. MwSt." : "incl. BTW";

    return `${formattedPrice} ${vatLabel}`;
  } catch (error) {
    console.error("Error calculating VAT price:", error);
    return priceString;
  }
}


  /**
   * Extracts product data from the current page (same approach as upsell products)
   * @param {string} productId - The product ID
   * @param {string} variantId - The variant ID
   * @returns {Object|null} Product data object or null if not found
   */
  #extractProductDataFromPage(productId, variantId) {
    console.log("Extracting product data for:", { productId, variantId });
    
    // Try multiple approaches to find product data on the current page
    
    // 1. Look for product-form-component with the product ID
    const productForm = document.querySelector(`product-form-component[data-product-id="${productId}"]`);
    if (productForm) {
      console.log("Found product-form-component:", productForm);
      
      // Get title from page heading or product title
      const title = 
        document.querySelector("h1")?.textContent?.trim() ||
        document.querySelector("[data-product-title]")?.textContent?.trim() ||
        document.querySelector(".product-title")?.textContent?.trim();
      
      // Get price from price elements
      const price = 
        document.querySelector(".price")?.textContent?.trim() ||
        document.querySelector("[data-price]")?.textContent?.trim() ||
        document.querySelector(".product-price")?.textContent?.trim();
      
      // Get image from product media
      const image = 
        document.querySelector(".product-media img")?.src ||
        document.querySelector("[data-product-image] img")?.src ||
        document.querySelector(".product-image img")?.src ||
        document.querySelector("img[alt*='product']")?.src;
      
      // Get variant info
      const variant = 
        document.querySelector(".variant-picker")?.textContent?.trim() ||
        document.querySelector("[data-variant]")?.textContent?.trim();
      
      if (title) {
        console.log("Extracted product data:", { title, price, image, variant });
        return {
          title: title,
          price: price || "",
          image: image || "",
          variant: variant || "",
        };
      }
    }
    
    // 2. Look for any element with data-product-id
    const productElement = document.querySelector(`[data-product-id="${productId}"]`);
    if (productElement) {
      console.log("Found product element:", productElement);
      
      const title = productElement.querySelector("h1, h2, h3")?.textContent?.trim();
      const price = productElement.querySelector(".price")?.textContent?.trim();
      const image = productElement.querySelector("img")?.src;
      
      if (title) {
        return {
          title: title,
          price: price || "",
          image: image || "",
          variant: "",
        };
      }
    }
    
    // 3. Fallback: try to get basic info from page
    const title = document.querySelector("h1")?.textContent?.trim();
    const price = document.querySelector(".price")?.textContent?.trim();
    const image = document.querySelector("img")?.src;
    
    if (title) {
      console.log("Using fallback data:", { title, price, image });
      return {
        title: title,
        price: price || "",
        image: image || "",
        variant: "",
      };
    }
    
    console.log("No product data found on page");
    return null;
  }

  /**
   * Fetches product data from API as fallback
   * @param {string} productId - The product ID
   * @param {string} variantId - The variant ID
   * @param {number} quantity - The quantity
   */
  async #fetchProductDataFromAPI(productId, variantId, quantity) {
    try {
      // Fetch the product data to get full information
      const response = await fetch(`/products/${productId}.js`);
      const product = await response.json();

      // Find the specific variant if variantId is provided
      let variant = product.variants?.[0]; // Default to first variant
      if (variantId && product.variants) {
        variant =
          product.variants.find(
            (v) => v.id.toString() === variantId.toString()
          ) || variant;
      }

      // Get the best image for this variant
      let productImage = product.featured_image || product.image;
      if (variant && variant.featured_image) {
        productImage = variant.featured_image;
      }

      // Format variant options (size, color, etc.)
      const variantOptions = this.#formatVariantOptions(variant);

      const addedProductHTML = `
        <div class="upsell-added-product">
          <h3 class="upsell-added-product__header">Artikel toegevoegd aan winkelwagen</h3>
          <div class="upsell-added-product__image">
            <img 
              src="${productImage}" 
              alt="${product.title}"
              loading="lazy"
            />
          </div>
          <div class="upsell-added-product__content">
            <h3 class="upsell-added-product__title">${product.title}</h3>
           
          </div>
        </div>
      `;

      this.addedProductContainer.innerHTML = addedProductHTML;
    } catch (error) {
      console.error("Error fetching product data from API:", error);
      throw error; // Re-throw to be handled by the calling method
    }
  }

  /**
   * Adds a test button for debugging the upsell drawer
   */
}

customElements.define("upsell-drawer-component", UpsellDrawerComponent);
